<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { getContactSaveErrorMessage } from 'dashboard/helper/contactErrorMessage';

import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import ViewSelector from 'dashboard/components-next/select/ViewSelector.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';

import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';
import RetinoTickets from 'dashboard/routes/dashboard/conversation/RetinoTickets.vue';
import CustomDataProvider from 'dashboard/routes/dashboard/conversation/CustomDataProvider.vue';
import OrderHistory from '../../conversation/OrderHistory.vue';
import { useSnakeCase } from '../../../../composables/useTransformKeys';

const store = useStore();
const route = useRoute();
const router = useRouter();

const contact = useMapGetter('contacts/getContactById');
const rawContact = useMapGetter('contacts/getContact');
const uiFlags = useMapGetter('contacts/getUIFlags');

const activeTab = ref('history');
const contactMergeRef = ref(null);
const showMergeModal = ref(false);

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);

const selectedContact = computed(() => contact.value(route.params.contactId));
// The merge modal (shared with the conversation sidebar) expects the
// snake_case record as stored, not the camelCased copy used by this page.
const mergeModalContact = computed(() =>
  rawContact.value(route.params.contactId)
);

const showSpinner = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const { t } = useI18n();

const getIntegrationGetter = useMapGetter('integrations/getIntegration');
const retinoIntegration = computed(() =>
  getIntegrationGetter.value('retino', null)
);
const customDataProviderIntegration = computed(() =>
  getIntegrationGetter.value('custom_data_provider', null)
);
const customDataProviderHooks = computed(() =>
  customDataProviderIntegration.value?.enabled
    ? customDataProviderIntegration.value.hooks
    : []
);

const CUSTOM_DATA_TAB_PREFIX = 'custom_data_provider_';

const CONTACT_TABS_OPTIONS = [
  { key: 'ATTRIBUTES', value: 'attributes' },
  { key: 'HISTORY', value: 'history' },
  { key: 'ORDER_HISTORY', value: 'order_history' },
  { key: 'NOTES', value: 'notes' },
  { key: 'MERGE', value: 'merge' },
];

const tabs = computed(() => {
  const options = [...CONTACT_TABS_OPTIONS];
  if (retinoIntegration.value?.enabled) {
    options.push({ key: 'RETINO', value: 'retino' });
  }

  const standardTabs = options.map(tab => ({
    label: t(`CONTACTS_LAYOUT.SIDEBAR.TABS.${tab.key}`),
    value: tab.value,
  }));

  // Each custom data hook gets its own line in the dropdown, labeled by name.
  const customDataTabs = customDataProviderHooks.value.map(hook => ({
    label:
      hook.settings?.name ||
      t('CONVERSATION_SIDEBAR.ACCORDION.CUSTOM_DATA_PROVIDER'),
    value: `${CUSTOM_DATA_TAB_PREFIX}${hook.id}`,
  }));

  return [...standardTabs, ...customDataTabs];
});

const activeCustomDataHook = computed(() => {
  if (!activeTab.value.startsWith(CUSTOM_DATA_TAB_PREFIX)) return null;
  const hookId = Number(activeTab.value.slice(CUSTOM_DATA_TAB_PREFIX.length));
  return customDataProviderHooks.value.find(hook => hook.id === hookId) || null;
});

// The custom data tab fills the sidebar height so its iframe can use it all.
const isFullHeightTab = computed(() => !!activeCustomDataHook.value);

const goToContactsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const fetchActiveContact = async () => {
  if (route.params.contactId) {
    await store.dispatch('contacts/show', { id: route.params.contactId });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      route.params.contactId
    );
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const openMergeModal = () => {
  showMergeModal.value = true;
};

const closeMergeModal = () => {
  showMergeModal.value = false;
};

// The contact shown here was deleted by the merge, so move on to the one
// that survived (same route type, so segment/label context is kept).
const onContactMerged = parentContactId => {
  closeMergeModal();
  router.replace({
    name: route.name,
    params: { ...route.params, contactId: parentContactId },
    query: route.query,
  });
};

const fetchContactNotes = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactNotes/get', { contactId });
};

const fetchContactConversations = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactConversations/get', contactId);
};

const fetchAttributes = () => {
  store.dispatch('attributes/get');
};

const toggleContactBlock = async isBlocked => {
  const ALERT_MESSAGES = {
    success: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    await store.dispatch(`contacts/update`, {
      ...selectedContact.value,
      blocked: !isBlocked,
    });
    useAlert(
      isBlocked ? ALERT_MESSAGES.success.unblock : ALERT_MESSAGES.success.block
    );
  } catch (error) {
    // Blocking re-saves the whole contact, so it fails for the same reasons
    // as an edit (e.g. an email already used by another contact).
    useAlert(
      getContactSaveErrorMessage(
        error,
        t,
        isBlocked ? ALERT_MESSAGES.error.unblock : ALERT_MESSAGES.error.block
      )
    );
  }
};

const toggleContactDomainBlock = async isDomainBlocked => {
  const ALERT_MESSAGES = {
    success: {
      block: t('CONTACT_PANEL.CONTACT_DOMAIN_BLOCKED_SUCCESS'),
      unblock: t('CONTACT_PANEL.CONTACT_DOMAIN_UNBLOCKED_SUCCESS'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    if (isDomainBlocked) {
      await store.dispatch(
        'contacts/unblockContactDomain',
        selectedContact.value.id
      );
    } else {
      await store.dispatch(
        'contacts/blockContactDomain',
        selectedContact.value.id
      );
    }
    useAlert(
      isDomainBlocked
        ? ALERT_MESSAGES.success.unblock
        : ALERT_MESSAGES.success.block
    );
  } catch (error) {
    // The API refuses to block a public (freemail) domain with an explanation.
    useAlert(
      error?.response?.data?.error ||
        (isDomainBlocked
          ? ALERT_MESSAGES.error.unblock
          : ALERT_MESSAGES.error.block)
    );
  }
};

const fetchContactData = () => {
  fetchActiveContact();
  fetchContactNotes();
  fetchContactConversations();
};

onMounted(() => {
  fetchContactData();
  fetchAttributes();
});

// The same view instance is reused when only the contact id changes (e.g.
// after a merge), so refetch instead of showing the previous contact's data.
watch(
  () => route.params.contactId,
  (contactId, previousContactId) => {
    if (contactId && contactId !== previousContactId) fetchContactData();
  }
);
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <ContactsDetailsLayout
      :button-label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
      :selected-contact="selectedContact"
      is-detail-view
      :show-pagination-footer="false"
      @go-to-contacts-list="goToContactsList"
      @toggle-block="toggleContactBlock"
      @toggle-domain-block="toggleContactDomainBlock"
      @merge-contact="openMergeModal"
    >
      <div
        v-if="showSpinner"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <ContactDetails
        v-else-if="selectedContact"
        :selected-contact="selectedContact"
        @go-to-contacts-list="goToContactsList"
      />
      <template #sidebar>
        <div class="flex flex-col" :class="{ 'h-full': isFullHeightTab }">
          <div class="px-6 pb-3 border-b border-n-weak shrink-0">
            <ViewSelector
              v-model="activeTab"
              :options="tabs"
              @change="handleTabChange"
            />
          </div>
          <div
            v-if="isFetchingItem"
            class="flex items-center justify-center py-10 text-n-slate-11"
          >
            <Spinner />
          </div>
          <template v-else>
            <ContactCustomAttributes
              v-if="activeTab === 'attributes'"
              :selected-contact="selectedContact"
            />
            <ContactNotes v-if="activeTab === 'notes'" />
            <ContactHistory v-if="activeTab === 'history'" />
            <div v-if="activeTab === 'retino'" class="px-6 py-4">
              <RetinoTickets
                v-for="(hook, index) in retinoIntegration.hooks"
                :key="hook.id"
                :contact="selectedContact"
                :hook="hook"
                :class="index > 0 ? 'mt-4' : undefined"
              />
            </div>
            <div v-if="activeCustomDataHook" class="flex-1 min-h-0 px-6 py-4">
              <CustomDataProvider
                :key="activeCustomDataHook.id"
                full-height
                :contact-id="selectedContact.id"
                :hook="activeCustomDataHook"
              />
            </div>
            <div v-if="activeTab === 'order_history'" class="px-6 py-4">
              <OrderHistory :contact="useSnakeCase(selectedContact)" />
            </div>
            <ContactMerge
              v-if="activeTab === 'merge'"
              ref="contactMergeRef"
              :selected-contact="selectedContact"
              @go-to-contacts-list="goToContactsList"
              @reset-tab="handleTabChange(CONTACT_TABS_OPTIONS[0])"
            />
          </template>
        </div>
      </template>
    </ContactsDetailsLayout>
    <ContactMergeModal
      v-if="showMergeModal"
      :show="showMergeModal"
      :primary-contact="mergeModalContact"
      @close="closeMergeModal"
      @merged="onContactMerged"
    />
  </div>
</template>
